import {LoadingDots} from '@commercial-helios/ui/src/components/loading-dots'
import {tokens} from '@commercial-helios/ui/theme'
import * as EmailValidator from 'email-validator'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import NavatticDemo from '@/components/navattic-demo'
import SEO from '@/components/seo'
import type {ExploreProductPageQuery} from '@/graphql-types'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useSignup from '@/hooks/use-signup'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import Input from '@helios/shared/components/input'
import LocalizedLink from '@helios/shared/components/localized-link'
import {Modal, ModalActions, ModalContent} from '@helios/shared/components/modal'
import {Main} from '@helios/shared/components/skip-to-content'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {useRecaptchaHubspotSubmit} from '@helios/shared/hooks/use-recaptcha-hubspot-submit'
import {useSessionStorage} from '@helios/shared/hooks/use-storage'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {TextLink, Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {MarketingCampaigns} from '@helios/shared/types/marketing-campaigns'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import getEnvironmentField from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

type Props = PageProps<ExploreProductPageQuery>

const ExploreProduct = ({data}: Props) => {
    const content = data.prismicExploreProduct.data

    const isSmallScreen = useMediaQuery.max('sm')
    const [showFakeScreen, setShowFakeScreen] = React.useState<boolean>(false)
    const [redirectUrl, setRedirectUrl] = React.useState<string | null>(null)
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
    const [isMobileModalOpen, setIsMobileModalOpen] = React.useState(true)
    const [iFrameReady, setIFrameReady] = React.useState(false)
    const [sessionValue, setSessionValue] = useSessionStorage<{email: string}>('interactive-demo', {
        email: '',
    })
    const [isOpen, setIsOpen] = React.useState<boolean>(false)
    const {
        handleSubmit,
        loading: redirecting,
        error,
        email,
        handleEmailChange,
        setEmail,
    } = useSignup(
        {
            form: 'Interactive Demo',
        },
        getEnvironmentField(content.hubspot_form_id_production, content.hubspot_form_id_staging)
    )
    const {recaptchaHubspotSubmit, loading} = useRecaptchaHubspotSubmit()
    const globalStrings = useGlobalStrings()
    const isCompanyEmail = useIsCompanyEmail(email)

    React.useEffect(() => {
        setShowFakeScreen(
            (!isSmallScreen && isOpen) || (!isSmallScreen && !!sessionValue.email && !iFrameReady)
        )
    }, [iFrameReady, isOpen, sessionValue.email, isSmallScreen])

    React.useEffect(() => {
        if (sessionValue.email) {
            setEmail(sessionValue.email)
        } else {
            setIsOpen(true)
            track(TrackingEvent.InteractiveDemoGateActioned, {action: 'viewed'})
        }
    }, [sessionValue.email, setEmail])

    React.useEffect(() => {
        // The redirectUrl is set when the user clicks Sign up at the end of the demo flow.
        if (redirectUrl && !redirecting) {
            // If we get an api error from HS, we still redirect to product-web
            if (error) {
                window.location.assign(redirectUrl)
                return
            }

            setRedirectUrl(null)

            // handleSubmit internally redirects to product-web
            handleSubmit()
        }
    }, [redirectUrl, redirecting, error, handleSubmit])

    if (!content) {
        return handleNoPrismicData()
    }

    const handleModalSubmit = (event: React.FormEvent) => {
        event.preventDefault()

        setErrorMessage(null)

        if (!EmailValidator.validate(email)) {
            setErrorMessage(globalStrings.invalid_email_error)
            track(TrackingEvent.EmailInteractiveDemoActioned, {action: 'blocked'})

            return
        }

        if (!isCompanyEmail) {
            setErrorMessage(globalStrings.non_work_email_error)
            track(TrackingEvent.EmailInteractiveDemoActioned, {action: 'blocked'})

            return
        }

        recaptchaHubspotSubmit(
            {email},
            {latest_campaign: MarketingCampaigns.InteractiveDemo},
            () => {
                track(TrackingEvent.EmailInteractiveDemoActioned, {action: 'blocked'})
                setErrorMessage(globalStrings.something_went_wrong)
            },
            () => {
                track(TrackingEvent.EmailInteractiveDemoActioned, {action: 'submitted'})
                setSessionValue({email})
                setIsOpen(false)
            }
        )
    }

    const iframeSource = getEnvironmentField(
        content.navattic_link.url,
        content.navattic_link_staging.url
    )

    return (
        <>
            <PageStyle />

            <Main>
                <FullscreenWrapper>
                    {showFakeScreen && <FakeScreen />}

                    {isSmallScreen && isMobileModalOpen ? (
                        <MobileModalContainer>
                            <MobileModalContent>
                                <Illustration image={content.mobile_modal[0].image} aria-hidden />
                                <MobileMenuSubtitle variant="paragraph-base" color="muted" center>
                                    {content.mobile_modal[0].subtitle}
                                </MobileMenuSubtitle>
                                <StyledButton
                                    onClick={() => {
                                        track(
                                            TrackingEvent.InteractiveDemoMobileGateContinueToDemoActioned,
                                            {
                                                action: 'completed',
                                            }
                                        )
                                        setIsMobileModalOpen(false)
                                    }}
                                >
                                    {content.mobile_modal[0].link_text}
                                </StyledButton>
                                <TextLink
                                    as={LocalizedLink}
                                    variant="label-base"
                                    bgColor="lightMuted"
                                    onClick={() => {
                                        track(
                                            TrackingEvent.InteractiveDemoMobileGateReturnHomepageActioned,
                                            {
                                                action: 'completed',
                                            }
                                        )
                                    }}
                                    to="/"
                                >
                                    {content.mobile_modal[0].button_text}
                                </TextLink>
                            </MobileModalContent>
                        </MobileModalContainer>
                    ) : (
                        hasNonEmptyItems(content.modal) && (
                            <Modal
                                title={content.modal[0].title}
                                subtitle={content.modal[0].subtitle}
                                isOpen={isOpen}
                                withCloseButton={false}
                                image={content.modal[0].image}
                            >
                                <form onSubmit={handleModalSubmit} noValidate>
                                    <ModalContent>
                                        <Input
                                            type="email"
                                            label="Work email" // TODO: Replace with globalStrings.work_email after translations are live
                                            value={email}
                                            onChange={(value) =>
                                                handleEmailChange({
                                                    target: {value},
                                                } as React.ChangeEvent<HTMLInputElement>)
                                            }
                                            required
                                            error={errorMessage}
                                            autoFocus
                                            data-testid="modal-input"
                                        />
                                    </ModalContent>

                                    <ModalActions>
                                        <Button
                                            type="submit"
                                            loading={loading}
                                            data-testid="modal-submit-button"
                                        >
                                            {content.modal[0].cta_text}
                                        </Button>
                                    </ModalActions>
                                </form>
                            </Modal>
                        )
                    )}
                    {redirecting ? (
                        <LoadingDots />
                    ) : (
                        <NavatticDemo
                            style={{opacity: showFakeScreen ? 0 : 1}}
                            title={content.interactive_demo_frame_title}
                            src={iframeSource}
                            onIFrameReady={() => setIFrameReady(true)}
                            onNavigate={(url: string) => setRedirectUrl(url)}
                        />
                    )}
                </FullscreenWrapper>
            </Main>
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<ExploreProductPageQuery>) => {
    const pageData = data.prismicExploreProduct.data

    const iframeSource = getEnvironmentField(
        pageData.navattic_link.url,
        pageData.navattic_link_staging.url
    )

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        >
            <link rel="preconnect" href="https://pleo.navattic.com/" />
            <link rel="preload" href={iframeSource} as="document" />
        </SEO>
    )
}

const PageStyle = createGlobalStyle`
    html, body {
        overflow: hidden;
    }
`

const FullscreenWrapper = styled.div`
    position: fixed;
    inset: 0;
`

const sideMenuWidth = 260
const contentWidth = 680

const FakeScreen = styled.div`
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: ${sideMenuWidth}px auto;
    filter: blur(4px);

    &::before {
        content: '';
        background-color: ${tokens.shade300};
        background-image: url('https://images.prismic.io/commercial-helios/08730471-f6cc-48f1-9104-0353b74c1309_side-menu-bg.png?auto=compress,format&w=${sideMenuWidth}');
        background-repeat: no-repeat;
    }

    &::after {
        content: '';
        width: 88%;
        max-width: ${contentWidth}px;
        justify-self: center;
        background-image: url('https://images.prismic.io/commercial-helios/3b7652e6-61d0-44f3-8b49-80e689c3c99e_content-bg.jpg?auto=compress,format&w=${contentWidth}');
        background-size: contain;
        background-repeat: no-repeat;
    }
`

const MobileModalContainer = styled.div`
    display: grid;
    width: 100%;
    height: 100%;
    padding: 80px ${tokens.spacing24};
    background-color: ${tokens.shade000};
    justify-items: center;
`

const MobileModalContent = styled.div`
    display: grid;
    max-width: 375px;
    height: fit-content;
    justify-items: center;
    grid-gap: ${tokens.spacing20};
`

const MobileMenuSubtitle = styled(Text)`
    margin-bottom: ${tokens.spacing20};
`

const Illustration = styled(Image)`
    width: 120px;
    height: 120px;
    object-fit: contain;
`

const StyledButton = styled(Button)`
    width: 100%;
`

export const query = graphql`
    query ExploreProductPage($locale: String!) {
        prismicExploreProduct(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    url
                }
                navattic_link {
                    url
                }
                navattic_link_staging {
                    url
                }
                interactive_demo_frame_title
                hubspot_form_id_staging
                hubspot_form_id_production
                mobile_modal {
                    subtitle
                    button_text
                    link_text
                    image {
                        alt
                        url
                        copyright
                        dimensions {
                            height
                            width
                        }
                    }
                }
                modal {
                    title
                    subtitle
                    cta_text
                    image {
                        alt
                        url
                        copyright
                        dimensions {
                            height
                            width
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(ExploreProduct)
